import React from 'react';
import type { RefObject } from 'react';

import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import {
  type AriaOverlayProps,
  OverlayContainer,
  useModal,
  useOverlay,
  usePreventScroll
} from '@react-aria/overlays';
import { mergeProps } from '@react-aria/utils';

import useContainerClassname from './hooks/container-class';
import useUnderlayClassname from './hooks/underlay-class';

const dialogStyles = {
  underlay: {
    background: '#495569cc',
    display: 'flex',
    justifyContent: 'center'
  },
  container: {
    outline: 0,
    minWidth: '400px'
  }
};

export interface IProps {
  containerClass?: string;
  centered?: boolean;
  isDismissable?: boolean;
  isOpen: boolean;
  isSecure?: boolean;
  shouldCloseOnBlur?: boolean;
  shouldCloseOnInteractOutside?: AriaOverlayProps['shouldCloseOnInteractOutside'];
  width?: string;

  onClose?: () => void;
}

const DialogContainer: React.FC<React.PropsWithChildren<IProps>> = ({
  containerClass,
  centered,
  isSecure,
  width,
  ...props
}) => {
  const {
    children,
    isDismissable,
    shouldCloseOnBlur,
    shouldCloseOnInteractOutside,
    isOpen,
    onClose
  } = props;
  const dialogRef = React.useRef<HTMLDivElement>(null);

  const { dialogProps } = useDialog(
    {
      role: 'dialog'
    },
    dialogRef
  );
  const { overlayProps, underlayProps } = useOverlay(
    {
      isOpen,
      onClose,
      isDismissable: isDismissable,
      shouldCloseOnBlur: shouldCloseOnBlur,
      shouldCloseOnInteractOutside: shouldCloseOnInteractOutside
    },
    dialogRef
  );

  const underlayClassName = useUnderlayClassname(centered!, isSecure!);
  const containerClassName = useContainerClassname(containerClass!, width);

  usePreventScroll();

  const { modalProps } = useModal();

  if (!isOpen) return null;

  return (
    <OverlayContainer>
      <div style={dialogStyles.underlay} className={underlayClassName} {...underlayProps}>
        <FocusScope contain restoreFocus>
          <div
            {...mergeProps(overlayProps, dialogProps, modalProps)}
            ref={dialogRef}
            style={dialogStyles.container}
            className={containerClassName}
          >
            {children}
          </div>
        </FocusScope>
      </div>
    </OverlayContainer>
  );
};

DialogContainer.defaultProps = {
  centered: false,
  isDismissable: true,
  isSecure: false,
  shouldCloseOnBlur: false
};

export default DialogContainer;
